import { create } from "zustand";
import {
  datasetsForTable,
  type TableDatasetMembership,
} from "@/lib/api/datasets";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("DatasetMembership");

interface DatasetMembershipState {
  /** The datasets each table is in; absent until its first load lands. */
  membership: Record<string, TableDatasetMembership[]>;
  failed: Record<string, boolean>;
  /** Bumped by `invalidate` alone: a load is not a change. */
  changes: number;
  load: (tableId: string) => Promise<void>;
  /** After a write that put these tables in a dataset or took them out. */
  invalidate: (tableIds: readonly string[]) => void;
}

const inFlight = new Map<string, Promise<void>>();

export const useDatasetMembershipStore = create<DatasetMembershipState>(
  (set, get) => ({
    membership: {},
    failed: {},
    changes: 0,
    load: (tableId) => {
      const pending = inFlight.get(tableId);
      if (pending) return pending;
      const request: Promise<void> = datasetsForTable(tableId)
        .then(
          (datasets) => {
            if (inFlight.get(tableId) !== request) return;
            set((s) => ({
              membership: { ...s.membership, [tableId]: datasets },
              failed: { ...s.failed, [tableId]: false },
            }));
          },
          (err) => {
            if (inFlight.get(tableId) !== request) return;
            log.error("Failed to load datasets for table %s: %o", tableId, err);
            set((s) => ({ failed: { ...s.failed, [tableId]: true } }));
          },
        )
        .finally(() => {
          if (inFlight.get(tableId) === request) inFlight.delete(tableId);
        });
      inFlight.set(tableId, request);
      return request;
    },
    invalidate: (tableIds) => {
      for (const tableId of tableIds) {
        // A load already out may have been answered before the write landed.
        inFlight.delete(tableId);
        void get().load(tableId);
      }
      set((s) => ({ changes: s.changes + 1 }));
    },
  }),
);
